import type { RxCollection, MangoQuerySelector } from 'rxdb';
import type {
    FindingDocType,
    FindingPlanumDocType,
    FindingProfileDocType,
    MediaDocType,
    MediaLinkDocType,
    MediaGroupDocType,
    OverallDescriptionDocType,
    ProjectDocType
} from '@ast-data/shared';
import { composeFileName, fileNameSpan } from '../../../utils/downloadFile';
import { familyProjectIds } from '../../projects/family';
import { locationLabel as sharedLageLabel } from '../../findings/locationLabel';
import {
    findPlacedMedia,
    resolveExportMedia,
    type ExportMedia
} from '../pdf/exportMedia';

import { compareNatural } from '../../../utils/compare';
import { showDocs } from '../../users/personLabels';
import {
    createDeliverableTexts,
    type DeliverableTexts
} from '../../translation/deliverableTexts';

// ---------------------------------------------------------------------------
/**
 * Data layer for the Gesamtbeschreibung PDF export: gathers what a sheet needs
 * into a plain, RxDB-free structure the react-pdf layout
 * (OverallDescriptionPdfDocument.tsx) consumes.
 *
 * The references are the one part not simply read off the record: a
 * Gesamtbeschreibung points at Befunde AND at single Planum/Profil
 * descriptions by id, and a sheet has to name them as a reader knows them
 * ("Bef. 7000, Fl. 1, Pl. 1"). Those live in OTHER records, family-wide, which
 * is why this reaches past the project.
 */
// ---------------------------------------------------------------------------

/** One Gesamtbeschreibung = one sheet (or more, if the text runs long). */
export interface ExportOverallDescription {
    key: string;
    doc: OverallDescriptionDocType;
    /**
     * The Maßnahme THIS record belongs to — per sheet, not per export, so a
     * selection made in the cross-project Auswertung prints as one document
     * whose sheets each say where their record is from.
     */
    project: ProjectDocType;
    /** What it covers, in reading order: whole Befunde, then descriptions. */
    references: string[];
    /** Sketches + photos of this record, createdAt-ascending. */
    media: ExportMedia[];
}

export interface OverallDescriptionExportData {
    overallDescriptions: ExportOverallDescription[];
}

/** The collections `collectOverallDescriptionExportData` reads. */
export interface OverallDescriptionExportCollections {
    projects: RxCollection<ProjectDocType>;
    overallDescriptions: RxCollection<OverallDescriptionDocType>;
    findings: RxCollection<FindingDocType>;
    planums: RxCollection<FindingPlanumDocType>;
    profiles: RxCollection<FindingProfileDocType>;
    media: RxCollection<MediaDocType>;
    mediaLinks: RxCollection<MediaLinkDocType>;
    mediaGroups: RxCollection<MediaGroupDocType>;
}

/** Numeric where both sides are numbers ("2" before "10"), else alphabetic. */
function numberCompare(left: string, right: string): number {
    return compareNatural(left, right);
}

/**
 * The sheet's caption. The number leads — that is what a sheet is looked up by
 * — and the Bezeichnung follows where there is one, because "Gesb. 3" alone
 * tells a reader holding a loose page nothing.
 */
export function overallDescriptionSheetLabel(
    doc: OverallDescriptionDocType,
    numberLabel: string
): string {
    const number = doc.overallDescriptionNumber.trim();
    const head = number ? `${numberLabel} ${number}` : numberLabel;
    const designation = doc.designation.trim();
    return designation ? `${head} — ${designation}` : head;
}

/**
 * German throughout and independent of the UI language: an exported file keeps
 * its name once it is filed away, and shares the Befund documentation's
 * grammar so the two read as a pair in a folder.
 *
 * The Maßnahme leads the name only when the document is of ONE; a selection
 * spanning several is named by how many it covers, because a name claiming the
 * first would file the whole thing under a Maßnahme most of it is not from.
 */
export function overallDescriptionPdfFileName(
    measureNumbers: readonly string[],
    numbers: readonly string[]
): string {
    const measures = [...new Set(measureNumbers.filter(Boolean))];
    const labels = numbers.map((number) => {
        const trimmed = number.trim();
        return trimmed ? `Gesb-${trimmed}` : '';
    });
    return composeFileName(
        [
            measures.length === 1
                ? measures[0]
                : measures.length > 1
                  ? `${measures.length}Massnahmen`
                  : '',
            'Gesamtbeschreibungen',
            ...fileNameSpan(labels, 'Gesamtbeschreibungen')
        ],
        '.pdf'
    );
}

/** A collection's matching documents, people shown by their accounts. */
async function findAllShown<T>(
    collection: RxCollection<T>,
    selector: Record<string, unknown>,
    labelOf?: (id: string) => string
): Promise<T[]> {
    const docs = await collection
        .find({ selector: selector as MangoQuerySelector<T> })
        .exec();
    return showDocs<T>(collection.name, docs, labelOf);
}

/**
 * Gather the export payload for a set of Gesamtbeschreibungen, named by id —
 * id-driven rather than project-driven, because the cross-project Auswertung
 * ticks records across Maßnahmen.
 *
 * Records whose project is not in the local database are DROPPED: the sheet's
 * whole head comes from the project. An empty result therefore covers both
 * "nothing ticked that exists" and "nothing ticked at all".
 */
export async function collectOverallDescriptionExportData({
    collections,
    ids,
    labelOf,
    deliverable = createDeliverableTexts(null, null)
}: {
    collections: OverallDescriptionExportCollections;
    ids: readonly string[];
    /** Shows linked people by their accounts — see usePersonLabels. */
    labelOf?: (id: string) => string;
    /** Puts the accepted German translations in; without, the originals. */
    deliverable?: DeliverableTexts;
}): Promise<OverallDescriptionExportData> {
    const findAll = <T>(
        collection: RxCollection<T>,
        selector: Record<string, unknown>
    ) => findAllShown<T>(collection, selector, labelOf);
    if (ids.length === 0) {
        return { overallDescriptions: [] };
    }
    const docs = await deliverable.apply(
        'overallDescription',
        await findAll<OverallDescriptionDocType>(
            collections.overallDescriptions,
            { id: { $in: [...ids] } }
        )
    );
    if (docs.length === 0) {
        return { overallDescriptions: [] };
    }

    const allProjects = await findAll<ProjectDocType>(collections.projects, {});
    const projectById = new Map(
        allProjects.map((project) => [project.id, project])
    );

    // Maßnahme first, then number: a document gathered across projects reads
    // as one chapter per Maßnahme. Within one project it is plain number order.
    const printable = docs
        .filter((doc) => projectById.has(doc.projectId))
        .sort(
            (firstDoc, secondDoc) =>
                numberCompare(
                    projectById.get(firstDoc.projectId)?.measureNumber ?? '',
                    projectById.get(secondDoc.projectId)?.measureNumber ?? ''
                ) ||
                numberCompare(
                    firstDoc.overallDescriptionNumber,
                    secondDoc.overallDescriptionNumber
                )
        );
    if (printable.length === 0) {
        return { overallDescriptions: [] };
    }

    // A Befund is shared across the Teilmaßnahme family, so a referenced
    // description may live in a sibling phase: labels are collected family-wide
    // or a reference prints as a bare id nobody can read.
    const familyIds = [
        ...new Set(
            printable.flatMap((doc) =>
                familyProjectIds(
                    allProjects,
                    projectById.get(doc.projectId)?.familyId ?? '',
                    doc.projectId
                )
            )
        )
    ];
    const scope = { projectId: { $in: familyIds } };
    const [familyFindings, familyPlanums, familyProfiles] = await Promise.all([
        findAll<FindingDocType>(collections.findings, scope),
        findAll<FindingPlanumDocType>(collections.planums, scope),
        findAll<FindingProfileDocType>(collections.profiles, scope)
    ]);

    const findingNumberById = new Map(
        familyFindings.map((finding) => [finding.id, finding.findingNumber])
    );
    const descriptionLabelById = new Map<string, string>();
    for (const planum of familyPlanums) {
        descriptionLabelById.set(
            planum.id,
            sharedLageLabel({
                findingNumber: findingNumberById.get(planum.findingId) ?? '',
                area: planum.area,
                planumNumber: planum.planumNumber
            })
        );
    }
    for (const profile of familyProfiles) {
        descriptionLabelById.set(
            profile.id,
            sharedLageLabel({
                findingNumber: findingNumberById.get(profile.findingId) ?? '',
                area: profile.area,
                profileNumber: profile.profileNumber
            })
        );
    }

    const mediaFor = await resolveExportMedia(
        await findPlacedMedia(
            collections.media,
            collections.mediaLinks,
            collections.mediaGroups,
            printable.map((doc) => doc.id)
        )
    );

    return {
        overallDescriptions: printable.map((doc) => ({
            key: doc.id,
            doc,
            // Present — `printable` dropped every record whose project is not.
            project: projectById.get(doc.projectId) as ProjectDocType,
            references: referenceLabels(
                doc,
                findingNumberById,
                descriptionLabelById
            ),
            media: mediaFor(doc.id)
        }))
    };
}

/**
 * Whole Befunde first, then the single descriptions — the order the form asks
 * for them, coarse to fine. A reference whose record is not on this device is
 * left out rather than printed as an unreadable id.
 */
function referenceLabels(
    doc: OverallDescriptionDocType,
    findingNumberById: Map<string, string>,
    descriptionLabelById: Map<string, string>
): string[] {
    const findings = doc.findingIds
        .map((id) => findingNumberById.get(id))
        .filter((number): number is string => Boolean(number))
        .sort(numberCompare)
        // "Bef. 7000" — the chips' grammar, with nothing after the number.
        .map((findingNumber) => sharedLageLabel({ findingNumber }));
    const descriptions = [...doc.planumIds, ...doc.profileIds]
        .map((id) => descriptionLabelById.get(id))
        .filter((label): label is string => Boolean(label))
        .sort(numberCompare);
    return [...findings, ...descriptions];
}
